import React, { type ReactNode } from 'react';
import {
  CustomerServiceOutlined,
  RightOutlined,
  ShoppingOutlined,
} from '@ant-design/icons';

import type { PosItem } from '../api/usePosItemsSearch';
import type { PosSaleLine } from '../types';
import { getFormattedPrice } from '@tools/utils/common.tsx';
import { PosQuantityStepper } from './PosQuantityStepper';

import './PosSelectableItemRow.scss';

export interface PosSelectableItemRowProps {
  item: PosItem;
  currency?: string;
  cartLine?: PosSaleLine | null;
  accessory?: ReactNode;
  onAdd: () => void;
  onOpenVariants: () => void;
  onQuantityChange?: (quantity: number) => void;
  onRemove?: () => void;
  isExiting?: boolean;
  onFocusWithinChange?: (focused: boolean) => void;
}

export const PosSelectableItemRow: React.FC<PosSelectableItemRowProps> = ({
  item,
  currency,
  cartLine,
  accessory,
  onAdd,
  onOpenVariants,
  onQuantityChange,
  onRemove,
  isExiting = false,
  onFocusWithinChange,
}) => {
  const isGroup = item.service_kind === 'group';
  const isParent = item.is_parent === true;
  const hasChildren = (item.children?.length ?? 0) > 0;
  const isUnavailable = isGroup || (isParent && !hasChildren);
  const priceValue = item.price?.value ?? null;
  const itemTypeLabel = item.item_type === 'service' ? 'Servicio' : 'Producto';

  const renderAction = () => {
    if (isUnavailable) {
      return (
        <span className="pos-selectable-item-row__status">
          {isGroup ? 'Grupal' : 'Sin variantes disponibles'}
        </span>
      );
    }

    if (isParent) {
      return (
        <button
          type="button"
          className="pos-selectable-item-row__navigate"
          onClick={onOpenVariants}
          aria-label={`Abrir variantes de ${item.name}`}
        >
          <span>{hasChildren ? `${item.children?.length} variantes` : 'Variantes'}</span>
          <RightOutlined aria-hidden />
        </button>
      );
    }

    if (cartLine && onQuantityChange && onRemove) {
      return (
        <PosQuantityStepper
          quantity={cartLine.quantity}
          itemName={item.name}
          onChange={onQuantityChange}
          onRemove={onRemove}
          className="pos-selectable-item-row__quantity"
        />
      );
    }

    return (
      <button
        type="button"
        className="pos-selectable-item-row__add"
        onClick={onAdd}
        aria-label={`Agregar ${item.name}`}
      >
        Agregar
      </button>
    );
  };

  return (
    <article
      className={`pos-selectable-item-row${isUnavailable ? ' pos-selectable-item-row--disabled' : ''}${isExiting ? ' pos-selectable-item-row--exiting' : ''}`}
      onFocusCapture={() => onFocusWithinChange?.(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          onFocusWithinChange?.(false);
        }
      }}
    >
      <div className="pos-selectable-item-row__image-wrapper">
        {item.thumbnail_url ? (
          <img src={item.thumbnail_url} alt={item.name} className="pos-selectable-item-row__image" />
        ) : item.item_type === 'service' ? (
          <CustomerServiceOutlined aria-label="Servicio" />
        ) : (
          <ShoppingOutlined aria-label="Producto" />
        )}
      </div>
      <div className="pos-selectable-item-row__body">
        <div className="pos-selectable-item-row__name" title={item.name}>{item.name}</div>
        <div className="pos-selectable-item-row__meta">
          {item.sku ? <span>{item.sku}</span> : null}
          <span>{item.sku ? ' · ' : ''}{itemTypeLabel}</span>
          {accessory ? <span className="pos-selectable-item-row__accessory">{accessory}</span> : null}
        </div>
      </div>
      <div className="pos-selectable-item-row__price">
        {isParent ? (
          <span className="pos-selectable-item-row__variants-count">
            {hasChildren ? `${item.children?.length} variantes` : 'Sin variantes disponibles'}
          </span>
        ) : (
          <span>{priceValue !== null ? getFormattedPrice(priceValue, item.price?.currency ?? currency) : 'Sin precio'}</span>
        )}
      </div>
      <div className="pos-selectable-item-row__action">{renderAction()}</div>
    </article>
  );
};

export default PosSelectableItemRow;
